import { Youtube, Facebook, Instagram, Mail, Phone, MapPin, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer id="give" className="footer-section" data-testid="footer">
      {/* Give Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-overline text-white/50 mb-4">Support Our Mission</p>
              <h2 className="heading-massive text-4xl sm:text-5xl text-white mb-6" data-testid="give-heading">
                Partner<br />
                with us.
              </h2>
              <p className="font-['Satoshi'] text-white/70 text-lg leading-relaxed max-w-md">
                Your generosity enables us to spread the gospel, serve our community, and transform lives in New Orleans and beyond.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <a href="https://give.tithe.ly/" target="_blank" rel="noopener noreferrer">
                <Button
                  className="btn-sharp bg-[#FFB800] text-black hover:bg-white px-8 py-6 text-sm flex items-center gap-2 w-full sm:w-auto"
                  data-testid="give-online-btn"
                >
                  <Heart size={18} />
                  Give Online
                </Button>
              </a>
              <a href="#visit">
                <Button
                  variant="outline"
                  className="btn-sharp border-white text-white hover:bg-white hover:text-black px-8 py-6 text-sm w-full sm:w-auto"
                  data-testid="give-in-person-btn"
                >
                  Give In Person
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Logo & Tagline */}
          <div className="md:col-span-2">
            <h3 className="font-['Clash_Display'] font-bold text-3xl text-white mb-4" data-testid="footer-logo">
              LIGHT CITY
            </h3>
            <p className="font-['Satoshi'] text-white/60 text-lg mb-6 max-w-sm">
              Bringing light to the city. A gospel-centric community dedicated to transforming lives.
            </p>
            {/* Social Icons */}
            <div className="flex gap-4" data-testid="footer-social">
              <a
                href="https://www.youtube.com/@leonardlucas2293"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon text-white"
                data-testid="footer-youtube"
              >
                <Youtube size={24} />
              </a>
              <a
                href="https://www.facebook.com/LightCityNola"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon text-white"
                data-testid="footer-facebook"
              >
                <Facebook size={24} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon text-white"
                data-testid="footer-instagram"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Clash_Display'] font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "About", href: "#mission" },
                { name: "Messages", href: "#media" },
                { name: "Plan Your Visit", href: "#visit" },
                { name: "Leadership", href: "#leadership" },
                { name: "Give", href: "#give" },
              ].map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="font-['Satoshi'] text-white/60 hover:text-[#FFB800] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Clash_Display'] font-semibold text-white mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-[#FFB800] flex-shrink-0 mt-1" />
                <span className="font-['Satoshi'] text-white/60">
                  8900 Hayne Boulevard<br />
                  New Orleans, LA
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="text-[#FFB800]" />
                <a
                  href="mailto:lightcitychurchnola@gmail.com"
                  className="font-['Satoshi'] text-white/60 hover:text-[#FFB800] transition-colors"
                  data-testid="footer-email"
                >
                  lightcitychurchnola@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-[#FFB800]" />
                <a
                  href="tel:+15046038249"
                  className="font-['Satoshi'] text-white/60 hover:text-[#FFB800] transition-colors"
                  data-testid="footer-phone"
                >
                  (504) 603-8249
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-['Satoshi'] text-white/40 text-sm" data-testid="footer-copyright">
            © {currentYear} Light City Church. All rights reserved.
          </p>
          <p className="font-['Satoshi'] text-white/40 text-sm">
            8900 Hayne Boulevard, New Orleans, Louisiana
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
